// Symbolic text boxes (TT-1269, Eileen's sketches): a text box can SAY
// what its text is — a thought cloud for ideas, a sun for a vision, an
// arrow for a direction, a scroll for history, an open book for a
// reference, a burst for an insight. The names are the meaning, not the
// shape: a planner names an intent, the renderer draws it. The sun, scroll and
// book are painted pieces only (motifArt.js, the text-box manifest); a
// host without the art draws them as a plain box (TT-1333: their drawn
// versions read worse than plain). The cloud, arrow and burst are drawn
// here: path data in the box's own coordinates, like terrain.js, so a motif
// resizes with the box (board chunks grow, scene ladders pick widths) and
// rides the same ink pass and hand wobble as the plain box it replaces.
//
// Pure path data, no DOM. `rand` is any seeded () => [0, 1). A kind that is
// not a motif returns null / [] / zero reach everywhere, so callers fall
// through to terrain or their plain box.
//
// A motif paints OUTSIDE its rect — rays, scallops, an arrowhead — by
// `motifReach`, which layout uses to keep neighbours clear, the way the
// scene's cloudReach does.

import { jitter, pt, scallop, terrainOutline, walk } from './terrain.js';

// A text box's intent: what its text is for, which the box is drawn as.
// The intents, in the planner's order (platform domain/scenegen/board.py INTENTS).
export const INTENTS = ['idea', 'vision', 'direction', 'history', 'reference', 'insight'];
export const isIntent = (kind) => INTENTS.includes(kind);
// What each intent's words are for, as the planners' prompts put it.
export const INTENT_FOR = {
  idea: 'a notion still forming',
  vision: 'where the room wants to go',
  direction: 'the next steps',
  history: 'how we got here',
  reference: 'a source cited',
  insight: 'the a-ha',
};
/** A plan item's intent; one stored before TT-1335 names it `mood`. */
export const intentOf = (item) => item.intent ?? item.mood;

// The drawn motifs' inks: fill = the wash inside. The burst's rays are
// two-tone, warm gold so they read as light: a wide pale glow under a
// thinner amber line.
export const MOTIF_TINT = {
  idea: { fill: '#f5f2fb' },
  direction: { fill: '#ffffff' },
  insight: { fill: '#fffbe6', trim: '#d9a21b', glow: '#f3d98b' },
};
/** An intent drawn as paths here; the others are painted only (a plain box without their art). */
export const isDrawn = (kind) => Boolean(MOTIF_TINT[kind]);
// no paint outside the rect: a plain box, a terrain, a kind that is not a motif
export const NO_REACH = Object.freeze({ top: 0, right: 0, bottom: 0, left: 0 });

/**
 * How a text box's outline is inked, for any dress (a plain box and a
 * terrain take the default): `shade` on or off, the stroke's width (0 for
 * none), and `ink` when it is not the palette's ink. The burst has no ring —
 * its rays and wash are the shape; the arrow is a touch heavier so its head
 * holds up.
 */
export function motifStyle(kind) {
  switch (kind) {
    case 'insight':
      return { shade: false, strokeWidth: 0 };
    case 'direction':
      return { shade: true, strokeWidth: 2.6 };
    default:
      return { shade: true, strokeWidth: 2 };
  }
}

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// ---- the shapes' measures (shared by the path, the trim and the reach) ----

// The burst: an ellipse a little outside the rect (the text's own padding
// keeps the corners clear of the curve), rays from BURST_GAP past it, each
// 0.45 × the rect's short side within [18, 54].
const burstRing = (r) => ({ cx: r.x + r.w / 2, cy: r.y + r.h / 2, rx: (r.w / 2) * 1.05, ry: (r.h / 2) * 1.12 });
const BURST_GAP = 12;
const burstRay = (r) => clamp(Math.min(r.w, r.h) * 0.45, 18, 54);
// the ring's overhang plus the rays, on each side
function burstReach(r) {
  const { rx, ry } = burstRing(r);
  const out = BURST_GAP + burstRay(r) + 2;
  return { top: ry - r.h / 2 + out, bottom: ry - r.h / 2 + out, left: rx - r.w / 2 + out, right: rx - r.w / 2 + out };
}
const arrowHead = (r) => clamp(r.h * 0.7, 22, 70);
const arrowFlare = (r) => clamp(r.h * 0.5, 14, 40);

/**
 * Units of paint outside the rect on each side, so layout can inflate a
 * motif's rect before placing neighbours. Zero all round for a box that
 * draws no motif (a painted intent's reach is its piece's, motifArt.js).
 */
export function motifReach(kind, r) {
  switch (kind) {
    case 'idea': {
      const b = scallop(r) * 0.75; // a scallop peaks at half its control, which sits up to 1.2 × bump out
      return { top: b, right: b, bottom: b, left: b };
    }
    case 'direction':
      return { top: arrowFlare(r), bottom: arrowFlare(r), left: 0, right: arrowHead(r) };
    case 'insight':
      return burstReach(r);
    default:
      return NO_REACH;
  }
}

// ---- outlines --------------------------------------------------------------

/** A closed ring on an ellipse, breathing a little (one slow bulge) and jittered. */
function ringPath({ cx, cy, rx, ry }, rand, wobble) {
  const n = Math.max(24, Math.round((rx + ry) / 6));
  const phase = rand() * Math.PI * 2;
  let d = '';
  for (let i = 0; i < n; i++) {
    const t = (i / n) * Math.PI * 2;
    const k = 1 + 0.035 * Math.sin(t * 2 + phase);
    d += `${i ? 'L' : 'M'}${pt(cx + Math.cos(t) * rx * k + jitter(rand, wobble), cy + Math.sin(t) * ry * k + jitter(rand, wobble))}`;
  }
  return d + 'Z';
}

/**
 * Direction: an arrow pointing right. The text rides the shaft; the head
 * flares past the rect's top and bottom.
 */
function arrowPath(r, rand, wobble) {
  const { x, y, w, h } = r;
  const head = arrowHead(r);
  const flare = arrowFlare(r);
  const x1 = x + w;
  const j = () => jitter(rand, wobble);
  let d = walk([[x, y, x1, y, 10]], rand, wobble);
  d += ` L${pt(x1 + j(), y - flare + j())} L${pt(x1 + head + j(), y + h / 2 + j())} L${pt(x1 + j(), y + h + flare + j())}`;
  d += walk([[x1, y + h, x, y + h, 10]], rand, wobble, ' L');
  return d + 'Z';
}

/**
 * The text box's silhouette for a motif, closed path data in the rect's
 * coordinates, or null for a kind that draws none. `wobble` is the
 * hand's jitter in units.
 */
export function motifOutline(kind, r, rand, wobble = 2.2) {
  switch (kind) {
    case 'idea':
      // the plain thought cloud — the terrain's sky ring in its own tint.
      // Eileen's sketch had loops on the bumps; drawn, they read as beads on
      // the outline, so the cloud keeps the ring and the intent lives in the tint
      return terrainOutline('sky', r, rand);
    case 'direction':
      return arrowPath(r, rand, wobble);
    case 'insight':
      // a paper patch under the text with no ink of its own (motifStyle)
      return ringPath(burstRing(r), rand, 0.5);
    default:
      return null;
  }
}

// ---- trim --------------------------------------------------------------------

/**
 * Rays around an ellipse: `n` dashes from `gap` past the ring, each its own
 * length (short ones among long), angles evenly spread then nudged.
 */
function rays({ cx, cy, rx, ry }, n, gap, len, rand, spread) {
  const out = [];
  const phase = rand() * Math.PI * 2;
  for (let i = 0; i < n; i++) {
    const t = phase + ((i + (rand() - 0.5) * spread) / n) * Math.PI * 2;
    const ux = Math.cos(t);
    const uy = Math.sin(t);
    // from the ring outward along the radial, the ring's radius at this angle
    const rr = 1 / Math.sqrt((ux * ux) / (rx * rx) + (uy * uy) / (ry * ry));
    const g = gap * (0.8 + rand() * 0.6);
    const l = len * (rand() < 0.3 ? 0.45 + rand() * 0.25 : 0.75 + rand() * 0.35);
    out.push(`M${pt(cx + ux * (rr + g), cy + uy * (rr + g))} L${pt(cx + ux * (rr + g + l), cy + uy * (rr + g + l))}`);
  }
  return out;
}

/**
 * Trim: the strokes drawn AFTER the outline (they may cross it); of the
 * drawn motifs only the burst has any. Each item: { d, stroke, fill,
 * strokeWidth, opacity, kind: 'stroke' | 'wash' } — the terrain trim's
 * shape, so one mount serves both.
 */
export function motifTrim(kind, r, rand) {
  switch (kind) {
    case 'insight': {
      // two passes per ray: the glow (wide, pale) under the line (thinner, amber)
      const { trim, glow } = MOTIF_TINT.insight;
      const ds = rays(burstRing(r), 14 + Math.round(rand() * 4), BURST_GAP, burstRay(r), rand, 0.3);
      return [
        ...ds.map((d) => ({ d, stroke: glow, fill: 'none', strokeWidth: 6, opacity: 0.85, kind: 'stroke' })),
        ...ds.map((d) => ({ d, stroke: trim, fill: 'none', strokeWidth: 2.2, opacity: 0.95, kind: 'stroke' })),
      ];
    }
    default:
      return [];
  }
}
